// TimelineComponent.tsx
import React, { useEffect, useRef, useState } from "react";
import Timeline, {
  DateHeader,
  TimelineHeaders,
  TimelineMarkers,
  CustomMarker,
  Unit,
} from "react-calendar-timeline";
import { format, millisecondsToHours } from "date-fns";
import "./timeline.scss";
import { VideoList } from "../../../../../api/types/CamerasAdminVM";
import CameraIcon from "../../../../../assets/svg/timeline_event.svg";
import CameraSelectedIcon from "../../../../../assets/svg/timeline_event_selected.svg";
// import ReactCalendarTimeline from "react-calendar-timeline/dist/lib/Timeline";
import { useTranslation } from "react-i18next";
import { toZonedTime } from "date-fns-tz";
import {
  dbTimeToJsTime,
  formattedDateTime,
  jsTimeToDBTime,
} from "../../../../../utils/dateTime";
import {
  convertToTZForDateFnsNoHook,
  useAccountTimezone,
} from "../../../../../hooks/timezone";
import { DeviceEvent } from "../../../../../api/types/Camera";
import Forward from "../../../../../assets/svg/ic_outline-forward.svg";
import Backward from "../../../../../assets/svg/ic_outline-back.svg";
import { CAMERA_CAPTURE_MODES } from "../../../constants";

export enum TimelineItemTypes {
  VIDEO = "video",
  EVENT = "event",
}
interface TimelineComponentProps {
  videoList: VideoList[] | [];
  eventList: DeviceEvent[];
  startTime: number;
  endTime: number;
  onEventSelect?: (event: DeviceEvent, time: number) => void;
  onTimelineClick: (videoTime: VideoList[], time: number) => void;
  selectedEvent: DeviceEvent | null;
  customSelectedTime?: number;
}

const TimelineComponent = (props: TimelineComponentProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.video_history.video_playback",
  });
  const {
    videoList,
    eventList,
    startTime,
    endTime,
    selectedEvent,
    onEventSelect,
    onTimelineClick,
    customSelectedTime,
  } = props;
  const { tzForDateFns } = useAccountTimezone();
  const zonedStartTime = toZonedTime(
    dbTimeToJsTime(startTime),
    convertToTZForDateFnsNoHook(tzForDateFns)
  );
  const zonedEndTime = toZonedTime(
    dbTimeToJsTime(endTime),
    convertToTZForDateFnsNoHook(tzForDateFns)
  );

  const minTime = zonedStartTime.getTime();
  const maxTime = zonedEndTime.getTime();
  const [visibleTime, setVisibleTime] = useState({
    visibleTimeStart: minTime,
    visibleTimeEnd: maxTime,
  });
  const timelineRef = useRef<any | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedTimeMs, setSelectedTimeMs] = useState<number | null>(null);

  useEffect(() => {
    if (customSelectedTime) {
      // let localTime = jsTimeToDBTime(customSelectedTime);
      let localTimeZoned = toZonedTime(
        dbTimeToJsTime(customSelectedTime ?? 0),
        convertToTZForDateFnsNoHook(tzForDateFns)
      );
      let localTime = jsTimeToDBTime(localTimeZoned.getTime());
      // check if the item clicked is a video storage timeline item(Light blue bar)
      const selectedvideoItems = videoItems?.filter(
        (item) => localTime >= item.start_time && localTime <= item.end_time
      );
      const videoListItem = videoList?.filter(
        (item) =>
          item.time.toString() === selectedvideoItems?.[0]?.id.toString()
      );
      onTimelineClick(videoListItem, customSelectedTime);
      setSelectedTimeMs(customSelectedTime);
    }
  }, [customSelectedTime]);

  useEffect(() => {
    const localStart = toZonedTime(
      dbTimeToJsTime(selectedEvent?.timestamp ?? 0),
      convertToTZForDateFnsNoHook(tzForDateFns)
    );
    setSelectedTimeMs(localStart.getTime());
  }, [selectedEvent, tzForDateFns]);

  // Single group to show all items in one row
  const groups = [{ id: 1, title: "Trip Events" }];

  // Map events to timeline items
  const videoItems =
    videoList.map((event, idx) => {
      const start = toZonedTime(
        dbTimeToJsTime(event.time),
        convertToTZForDateFnsNoHook(tzForDateFns)
      );
      const end = toZonedTime(
        dbTimeToJsTime(event.time) + event.duration * 1000, // duration is in seconds, convert to milliseconds
        convertToTZForDateFnsNoHook(tzForDateFns)
      );
      return {
        id: event.time,
        group: 1,
        title: format(start, "HH:mm"),
        type: TimelineItemTypes.VIDEO,
        start_time: start.getTime(),
        end_time: end.getTime(),
        selectable: false,
        className: "bg-blue-100 h-full w-full",
      };
    }) || [];

  const eventItems =
    eventList.map((event, idx) => {
      const localStart = toZonedTime(
        dbTimeToJsTime(event?.timestamp),
        convertToTZForDateFnsNoHook(tzForDateFns)
      );
      const start = localStart.getTime();
      const end = localStart.getTime();

      return {
        id: idx,
        group: 1,
        title: event?.status?.description,
        type: TimelineItemTypes.EVENT,
        ticket: event.ticket, // Imp: Keep the event_ticket as id to map timeline items back to events
        recordId: event.recordid,
        start_time: start,
        end_time: end,
        className: "h-full w-full",
      };
    }) || [];

  const handleTimeChange = (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: any
  ) => {
    let start = minTime;
    let end = maxTime;
    if (visibleTimeStart < minTime && visibleTimeEnd > maxTime) {
      updateScrollCanvas(minTime, maxTime);
    } else if (visibleTimeStart < minTime) {
      end = minTime + (visibleTimeEnd - visibleTimeStart);
      updateScrollCanvas(
        minTime,
        minTime + (visibleTimeEnd - visibleTimeStart)
      );
    } else if (visibleTimeEnd > maxTime) {
      start = maxTime - (visibleTimeEnd - visibleTimeStart);
      updateScrollCanvas(
        maxTime - (visibleTimeEnd - visibleTimeStart),
        maxTime
      );
    } else {
      start = visibleTimeStart;
      end = visibleTimeEnd;
      updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
    }
    setVisibleTime({
      visibleTimeStart: start,
      visibleTimeEnd: end,
    });
  };

  useEffect(() => {
    if (timelineRef.current) {
      timelineRef?.current?.changeZoom(
        (maxTime - minTime) / 1000 / 60 / 5 // Set zoom to 5 minutes
      );
    }
  }, [maxTime, minTime, timelineRef]);

  const onItemClick = (
    itemId: string,
    e: React.MouseEvent | null,
    time: number
  ) => {
    // check if the item clicked is a video storage timeline item(Light blue bar)
    const selectedvideoItems = videoList?.filter(
      (item) => item.time.toString() === itemId.toString()
    );
    if (selectedvideoItems && selectedvideoItems.length > 0) {
      // If storage timeline items are found, call
      onTimelineClick(selectedvideoItems, time);
    }
    // Commenting the event selction as we will only select storage data from timeline
    // // If videotimeline was not found, find event from timeline event list
    // const timelineEvent = eventItems?.find(
    //   (item) => item.id.toString() === itemId.toString()
    // );
    // if (timelineEvent) {
    //   const userEvent = eventList?.find(
    //     (item) => item.ticket === timelineEvent?.ticket
    //   );
    //   if (userEvent) {
    //     onEventSelect?.(userEvent, time);
    //   }
    // }
    setSelectedTimeMs(time);
  };

  const getItemBackgroundColor = (item: any, itemContext: any) => {
    // if (item?.type === TimelineItemTypes.EVENT) {
    //   return selectedEvent?.ticket === item?.ticket ? "#304674" : "#0E88FF";
    // }
    return "rgba(14, 136, 255, 0.08)";
  };

  /**
   * Method to scroll forward/backward
   * @param isBack Flg to indicate backward scroll
   */
  const onScroll = (isBack = false) => {
    if (!scrollRef.current) return;
    const { visibleTimeStart, visibleTimeEnd } = visibleTime;
    const halfHourMs = 30 * 60 * 1000;
    let newStart = visibleTimeStart;
    let newEnd = visibleTimeEnd;

    if (isBack) {
      newStart = Math.max(minTime, visibleTimeStart - halfHourMs);
      newEnd = newStart + (visibleTimeEnd - visibleTimeStart);
      if (newEnd > maxTime) {
        newEnd = maxTime;
        newStart = maxTime - (visibleTimeEnd - visibleTimeStart);
      }
      if (newStart < minTime) {
        newStart = minTime;
        newEnd = minTime + (visibleTimeEnd - visibleTimeStart);
      }
    } else {
      newEnd = Math.min(maxTime, visibleTimeEnd + halfHourMs);
      newStart = newEnd - (visibleTimeEnd - visibleTimeStart);
      if (newStart < minTime) {
        newStart = minTime;
        newEnd = minTime + (visibleTimeEnd - visibleTimeStart);
      }
    }

    setVisibleTime({
      visibleTimeStart: newStart,
      visibleTimeEnd: newEnd,
    });

    if (scrollRef.current.scrollTo) {
      // Scroll the timeline horizontally to the new start time
      if (
        scrollRef.current &&
        typeof scrollRef.current.scrollTo === "function"
      ) {
        // Calculate the scroll position based on the new visible time range
        // This assumes the scroll container's scrollWidth matches the time range
        const scrollContainer = scrollRef.current;
        const totalDuration = maxTime - minTime;
        const scrollWidth =
          scrollContainer.scrollWidth - scrollContainer.clientWidth;
        // If scrollWidth is less than or equal to 0, no scrolling is needed
        if (scrollWidth <= 0) return;
        const percent = (newStart - minTime) / totalDuration;
        const left = Math.max(0, Math.min(scrollWidth, scrollWidth * percent));
        scrollRef.current.scrollTo({ left, behavior: "smooth" });
      }
    }
  };

  return (
    <>
      <div className="p-2 w-100% h-full bg-white rounded:lg m-2">
        <div className="flex flex-row self-end mb-2 justify-items-end items-end">
          <img
            src={Backward}
            alt="Back"
            className="w-5 h-5 mr-2 cursor-pointer"
            onClick={() => {
              onScroll(true);
            }}
          />
          <img
            src={Forward}
            alt="Forward"
            className="w-5 h-5 cursor-pointer"
            onClick={() => {
              onScroll();
            }}
          />
        </div>
        <Timeline
          className="w-full h-full"
          groups={groups}
          items={[...videoItems]}
          ref={timelineRef}
          scrollRef={(el: HTMLDivElement) => {
            scrollRef.current = el;
          }}
          // selected={[selectedEvent]}
          traditionalZoom={true}
          selected={[]}
          lineHeight={150}
          timeSteps={{
            minute: 5,
            hour: 1,
            second: 60,
            day: 1,
            month: 1,
            year: 1,
          }}
          minZoom={1000 * 60 * 60 * 2} // 2 hr in milliseconds
          maxZoom={1000 * 60 * 60 * 24} // 24 hr in milliseconds
          sidebarWidth={0}
          buffer={1}
          itemHeightRatio={0.75}
          rightSidebarWidth={0}
          defaultTimeStart={new Date(minTime)}
          defaultTimeEnd={new Date(maxTime)}
          visibleTimeStart={visibleTime.visibleTimeStart}
          visibleTimeEnd={visibleTime.visibleTimeEnd}
          // visibleTimeStart={minTime}
          // visibleTimeEnd={maxTime}
          onTimeChange={handleTimeChange}
          itemRenderer={({
            item,
            itemContext,
            getItemProps,
          }: {
            item: any;
            itemContext: any;
            getItemProps: any;
          }) => {
            return (
              <div
                {...getItemProps({
                  style: {
                    backgroundColor: getItemBackgroundColor(item, itemContext),
                    borderColor: "transparent",
                    color: "transparent",
                    cursor: "pointer",
                    height: "100%",
                    position: "relative",
                    overflow: "visible", // allow overflow
                  },
                })}
                onClick={(e) => {
                  // if (item?.type === TimelineItemTypes.EVENT) {
                  onItemClick(item.id, e, item.start_time);
                  // }
                }}
              >
                {/* {item?.type === "event" && (
                  <img
                    src={CameraIcon}
                    alt="event"
                    width={200}
                    // height="100%"
                    className="w-50 h-50"
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      transform: "translateX(-50%)",
                      zIndex: 200,
                      pointerEvents: "none",
                      backgroundColor: "transparent",
                    }}
                  />
                )} */}
              </div>
            );
          }}
          canMove={false}
          canResize={false}
        >
          <TimelineMarkers>
            <CustomMarker date={selectedTimeMs ?? 0}>
              {({ styles, date }) => {
                const customStyles = {
                  ...styles,
                  backgroundColor: "rgba(48, 70, 116, 1)",
                  width: "1px",
                  height: "100%",
                  zIndex: 150,
                };
                return <div style={customStyles} />;
              }}
            </CustomMarker>
            {eventItems?.map((event, index) => {
              return (
                <CustomMarker date={event.start_time}>
                  {({ styles, date }) => {
                    const customStyles = {
                      ...styles,
                      backgroundColor: "transparent",
                      width: "1px",
                      height: "100%",
                      overflow: "visible",
                      zIndex: 160 + index,
                    };
                    return (
                      <div style={customStyles}>
                        <img
                          src={
                            event.recordId === selectedEvent?.recordid
                              ? CameraSelectedIcon
                              : CameraIcon
                          }
                          alt="event"
                          className="w-5 h-5 absolute min-w-5 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                          onClick={() =>
                            onItemClick(
                              event?.id?.toString(),
                              null,
                              event.start_time
                            )
                          }
                        />
                      </div>
                    );
                  }}
                </CustomMarker>
              );
            })}
          </TimelineMarkers>

          <TimelineHeaders className="sticky">
            {/* <DateHeader unit="hour" className="sticky" /> */}
            <DateHeader
              unit="hour"
              className="sticky"
              labelFormat={(
                [startTime, endTime]: [moment.Moment, moment.Moment],
                unit: Unit,
                labelWidth: number
              ) => {
                // return `${formattedDateTime(startTime.hour(), "HH:mm")}`;
                return startTime.hour().toString();
              }}
              // labelFormat={(interval: any, intervalText: any) => {
              //   return `${(interval[0] as any)?.$H}`; // interval[0].$H was giving typescript error
              // }}
              // intervalRenderer={({
              //   getIntervalProps,
              //   intervalContext,
              //   data,
              // }) => {
              //   return (
              //     <div
              //       {...getIntervalProps()}
              //       className="flex flex-col items-center"
              //     >
              //       <h1 className="text-center text-xs">
              //         {`${intervalContext.intervalText}`}
              //       </h1>
              //       <div className="h-5 w-0.5 bg-blue-100 mb-(-10)" />
              //     </div>
              //   );
              // }}
            />
          </TimelineHeaders>
        </Timeline>
      </div>
    </>
  );
};

export default TimelineComponent;
